import { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  ChartBar,
  ArrowsClockwise,
  Fire,
  Gauge,
  TrendUp,
  TrendDown,
  Wrench,
  CurrencyEur,
  Lightning,
  Drop,
  Flame,
} from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { useMaintenanceStatistics } from "../hooks/useMaintenanceStatistics";
import { useEnergyPerformanceData } from "../hooks/useEnergyPerformanceData";
import { useMaintenanceCosts } from "../hooks/useMaintenanceCosts";
import {
  CheckCircle,
  Warning,
  XCircle,
  Calendar,
  Buildings,
  House,
} from "@phosphor-icons/react";
import { applyMask } from "shared/utils/masks";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// Brand colors
const COLORS = {
  moss: "#5B7304",
  brick: "#EA8162",
  neutral: "#31403B",
  muted: "#838C89",
  yellow: "#F59E0B",
  blue: "#3B82F6",
  green: "#22C55E",
  mossLight: "#F0F5E6",
  brickLight: "#F9F0EE",
};

// EPC color mapping for charts
const EPC_COLORS: Record<string, string> = {
  "A++": "#22C55E",
  "A+": "#22C55E",
  A: "#22C55E",
  B: "#5B7304",
  C: "#F59E0B",
  D: "#F97316",
  E: "#EA8162",
  F: "#EA8162",
  G: "#EA8162",
  Onbekend: "#838C89",
  "Niet van toepassing": "#838C89",
  "Gemeenschappelijke delen": "#31403B",
  Ontbrekend: "#D1D5DB",
};

const MaintenanceStatistieken = () => {
  const {
    statistics,
    isLoading: fireSafetyLoading,
    refresh: refreshFireSafety,
  } = useMaintenanceStatistics();
  const {
    metrics: epcMetrics,
    summaryIssues: epcSummary,
    isLoading: epcLoading,
    refresh: refreshEPC,
  } = useEnergyPerformanceData();
  const {
    costs,
    isLoading: costsLoading,
    refresh: refreshCosts,
  } = useMaintenanceCosts();

  const isLoading = fireSafetyLoading || epcLoading || costsLoading;

  const refresh = () => {
    refreshFireSafety();
    refreshEPC();
    refreshCosts();
  };

  // Initialize sticky top as 0 for this page
  useEffect(() => {
    document.documentElement.style.setProperty("--sticky-top", "0px");
  }, []);

  if (isLoading && !statistics) {
    return (
      <>
        <HelmetProvider>
          <Helmet>
            <title>Onderhoud Statistieken | Yally</title>
          </Helmet>
        </HelmetProvider>
        <div className="bg-background min-h-screen">
          <div className="container mx-auto px-4 py-6 lg:px-6">
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="bg-white">
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  const fireSafety = statistics?.fireSafety;

  // KPI Card data
  const kpiCards = [
    {
      title: "Totaal Items",
      value: fireSafety?.totalItems ?? 0,
      subtitle: `${fireSafety?.buildings ?? 0} gebouwen, ${fireSafety?.units ?? 0} units`,
      icon: <Buildings className="h-4 w-4" />,
      color: "text-neutral-primary",
      bg: "bg-white",
    },
    {
      title: "Conformiteitsgraad",
      value: `${fireSafety?.overallComplianceRate ?? 0}%`,
      subtitle: "Gewogen gemiddelde",
      icon: <CheckCircle className="h-4 w-4" />,
      color:
        (fireSafety?.overallComplianceRate ?? 0) >= 90
          ? "text-moss-primary"
          : (fireSafety?.overallComplianceRate ?? 0) >= 70
            ? "text-yellow-600"
            : "text-brick-primary",
      bg:
        (fireSafety?.overallComplianceRate ?? 0) >= 90
          ? "bg-moss-secondary-light"
          : (fireSafety?.overallComplianceRate ?? 0) >= 70
            ? "bg-yellow-50"
            : "bg-brick-secondary-fine",
    },
    {
      title: "Aandachtspunten",
      value:
        (fireSafety?.actionableIssues?.rookmelders ?? 0) +
        (fireSafety?.actionableIssues?.brandblussers ?? 0) +
        (fireSafety?.actionableIssues?.brandhaspel ?? 0) +
        (fireSafety?.actionableIssues?.noodverlichting ?? 0) +
        (fireSafety?.actionableIssues?.branddeuren ?? 0) +
        (fireSafety?.actionableIssues?.evacuatie ?? 0) +
        (fireSafety?.actionableIssues?.brandtrap ?? 0),
      subtitle: "Items niet in orde",
      icon: <XCircle className="h-4 w-4" />,
      color: "text-brick-primary",
      bg: "bg-brick-secondary-fine",
    },
    {
      title: "Keuringen Vervallen",
      value: fireSafety?.actionableIssues?.keuringVervallen ?? 0,
      subtitle: `${fireSafety?.actionableIssues?.keuring ?? 0} binnenkort`,
      icon: <Calendar className="h-4 w-4" />,
      color:
        (fireSafety?.actionableIssues?.keuringVervallen ?? 0) > 0
          ? "text-brick-primary"
          : "text-moss-primary",
      bg:
        (fireSafety?.actionableIssues?.keuringVervallen ?? 0) > 0
          ? "bg-brick-secondary-fine"
          : "bg-moss-secondary-light",
    },
  ];

  // Compliance by category chart data
  const complianceChartData = {
    labels: fireSafety?.complianceScores?.map((s) => s.category) ?? [],
    datasets: [
      {
        label: "Conformiteitsgraad (%)",
        data: fireSafety?.complianceScores?.map((s) => s.score) ?? [],
        backgroundColor:
          fireSafety?.complianceScores?.map((s) => s.color) ?? [],
        borderRadius: 4,
      },
    ],
  };

  const complianceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw}% conform`,
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          callback: (value: any) => `${value}%`,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Status distribution per category (stacked bar)
  const statusDistributionData = {
    labels: fireSafety?.categoryStatusCounts?.map((c) => c.category) ?? [],
    datasets: [
      {
        label: "In orde",
        data: fireSafety?.categoryStatusCounts?.map((c) => c.ok) ?? [],
        backgroundColor: COLORS.moss,
        borderRadius: 2,
      },
      {
        label: "Gedeeltelijk",
        data: fireSafety?.categoryStatusCounts?.map((c) => c.partial) ?? [],
        backgroundColor: COLORS.yellow,
        borderRadius: 2,
      },
      {
        label: "Niet in orde",
        data: fireSafety?.categoryStatusCounts?.map((c) => c.notOk) ?? [],
        backgroundColor: COLORS.brick,
        borderRadius: 2,
      },
      {
        label: "Onbekend",
        data: fireSafety?.categoryStatusCounts?.map((c) => c.unknown) ?? [],
        backgroundColor: COLORS.muted,
        borderRadius: 2,
      },
      {
        label: "Niet verplicht",
        data: fireSafety?.categoryStatusCounts?.map((c) => c.notRequired) ?? [],
        backgroundColor: "#E5E7EB",
        borderRadius: 2,
      },
    ],
  };

  const statusDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
    },
  };

  // Inspection status doughnut
  const inspectionDoughnutData = {
    labels: fireSafety?.inspectionStatus?.map((s) => s.status) ?? [],
    datasets: [
      {
        data: fireSafety?.inspectionStatus?.map((s) => s.count) ?? [],
        backgroundColor:
          fireSafety?.inspectionStatus?.map((s) => s.color) ?? [],
        borderWidth: 0,
      },
    ],
  };

  const inspectionDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          generateLabels: (chart: any) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => ({
                text: `${label}: ${data.datasets[0].data[i]}`,
                fillStyle: data.datasets[0].backgroundColor[i],
                hidden: false,
                index: i,
                pointStyle: "circle",
              }));
            }
            return [];
          },
        },
      },
    },
  };

  // Type distribution doughnut
  const typeDistributionData = {
    labels: fireSafety?.typeDistribution?.map((t) => t.type) ?? [],
    datasets: [
      {
        data: fireSafety?.typeDistribution?.map((t) => t.count) ?? [],
        backgroundColor: [COLORS.moss, COLORS.brick],
        borderWidth: 0,
      },
    ],
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Onderhoud Statistieken | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="bg-background min-h-screen">
        <div className="container mx-auto px-4 py-6 lg:px-6">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-moss-primary/10 rounded-lg p-2">
                <ChartBar
                  className="text-moss-primary h-6 w-6"
                  weight="duotone"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Onderhoud Statistieken
                </h1>
                <p className="text-muted-foreground text-sm">
                  Inzicht in brandveiligheid en energieprestaties
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={isLoading}
              className="gap-2"
            >
              <ArrowsClockwise
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                weight="bold"
              />
              Vernieuwen
            </Button>
          </div>

          {/* Fire Safety Section Header */}
          <div className="mb-6 flex items-center gap-3">
            <div className="bg-brick-primary/10 rounded-lg p-2">
              <Fire className="text-brick-primary h-6 w-6" weight="duotone" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">
                Brandveiligheid
              </h2>
              <p className="text-muted-foreground text-sm">
                Conformiteit en keuringstatus overzicht
              </p>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {kpiCards.map((card, index) => (
              <Card key={index} className={`${card.bg}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    {card.title}
                  </CardTitle>
                  <span className={card.color}>{card.icon}</span>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${card.color}`}>
                    {card.value}
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {card.subtitle}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Row 1: Compliance & Status Distribution */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Compliance by Category */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <CheckCircle
                    className="text-moss-primary h-5 w-5"
                    weight="duotone"
                  />
                  Conformiteitsgraad per Categorie
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Percentage items dat aan de eisen voldoet
                </p>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <Bar
                    data={complianceChartData}
                    options={complianceChartOptions}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Warning
                    className="h-5 w-5 text-yellow-600"
                    weight="duotone"
                  />
                  Status Verdeling per Categorie
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Aantal items per status per brandveiligheidscategorie
                </p>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <Bar
                    data={statusDistributionData}
                    options={statusDistributionOptions}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2: Inspections & Type Distribution */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Inspection Status */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Calendar
                    className="text-brick-primary h-5 w-5"
                    weight="duotone"
                  />
                  Keuringstatus Overzicht
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Status van geplande brandveiligheidskeuringen
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex h-[280px] items-center justify-center">
                  <Doughnut
                    data={inspectionDoughnutData}
                    options={inspectionDoughnutOptions}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Type Distribution */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Buildings
                    className="text-neutral-primary h-5 w-5"
                    weight="duotone"
                  />
                  Verdeling Gebouwen vs Units
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Aantal gebouwen en units in het systeem
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex h-[280px] items-center justify-center">
                  <Doughnut
                    data={typeDistributionData}
                    options={inspectionDoughnutOptions}
                  />
                </div>
                {/* Additional stats below doughnut */}
                <div className="border-neutral-primary/10 mt-4 grid grid-cols-2 gap-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-moss-primary flex items-center justify-center gap-2">
                      <Buildings className="h-4 w-4" weight="duotone" />
                      <span className="text-2xl font-bold">
                        {fireSafety?.buildings ?? 0}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Gebouwen
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="text-brick-primary flex items-center justify-center gap-2">
                      <House className="h-4 w-4" weight="duotone" />
                      <span className="text-2xl font-bold">
                        {fireSafety?.units ?? 0}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">Units</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Energy Performance Section Header */}
          <div className="mt-8 mb-6 flex items-center gap-3">
            <div className="bg-moss-primary/10 rounded-lg p-2">
              <Gauge className="text-moss-primary h-6 w-6" weight="duotone" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">
                Energieprestaties
              </h2>
              <p className="text-muted-foreground text-sm">
                EPC labels en energetische renovaties overzicht
              </p>
            </div>
          </div>

          {/* EPC KPI Cards */}
          {epcMetrics && (
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card className="bg-white">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    Units met EPC
                  </CardTitle>
                  <Gauge
                    className="text-moss-primary h-4 w-4"
                    weight="duotone"
                  />
                </CardHeader>
                <CardContent>
                  <div className="text-neutral-primary text-3xl font-bold">
                    {epcSummary.unitsWithEPC} / {epcSummary.totalUnits}
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {epcSummary.totalUnits > 0
                      ? Math.round(
                          (epcSummary.unitsWithEPC / epcSummary.totalUnits) *
                            100,
                        )
                      : 0}
                    % heeft EPC
                  </p>
                </CardContent>
              </Card>

              <Card
                className={
                  epcSummary.improved > 0
                    ? "bg-moss-secondary-light"
                    : "bg-white"
                }
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    EPC Verbeterd
                  </CardTitle>
                  <TrendUp
                    className="text-moss-primary h-4 w-4"
                    weight="duotone"
                  />
                </CardHeader>
                <CardContent>
                  <div className="text-moss-primary text-3xl font-bold">
                    {epcSummary.improved}
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Units met verbeterde EPC
                  </p>
                </CardContent>
              </Card>

              <Card
                className={
                  epcSummary.declined > 0
                    ? "bg-brick-secondary-fine"
                    : "bg-white"
                }
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    EPC Verslechterd
                  </CardTitle>
                  <TrendDown
                    className="text-brick-primary h-4 w-4"
                    weight="duotone"
                  />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-3xl font-bold ${epcSummary.declined > 0 ? "text-brick-primary" : "text-neutral-primary"}`}
                  >
                    {epcSummary.declined}
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Units met verslechterde EPC
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    Met Renovaties
                  </CardTitle>
                  <Wrench
                    className="text-neutral-primary h-4 w-4"
                    weight="duotone"
                  />
                </CardHeader>
              </Card>
            </div>
          )}

          {/* EPC Charts */}
          {epcMetrics && (
            <div className="mb-24 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Bar Chart - EPC Label Distribution */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Gauge
                      className="text-moss-primary h-5 w-5"
                      weight="duotone"
                    />
                    EPC Label Verdeling
                  </CardTitle>
                  <p className="text-muted-foreground text-sm">
                    Aantal units per EPC label
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="h-[280px]">
                    <Bar
                      data={{
                        labels: Object.keys(
                          epcMetrics.epcDistribution || {},
                        ).filter(
                          (label) => label !== "Gemeenschappelijke delen",
                        ),
                        datasets: [
                          {
                            label: "Aantal units",
                            data: Object.entries(
                              epcMetrics.epcDistribution || {},
                            )
                              .filter(
                                ([label]) =>
                                  label !== "Gemeenschappelijke delen",
                              )
                              .map(([, count]) => count),
                            backgroundColor: Object.keys(
                              epcMetrics.epcDistribution || {},
                            )
                              .filter(
                                (label) => label !== "Gemeenschappelijke delen",
                              )
                              .map((label) => EPC_COLORS[label] || "#838C89"),
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: "y" as const,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: (context: any) => {
                                const percentage = Math.round(
                                  (context.raw / epcMetrics.totalUnits) * 100,
                                );
                                return `${context.raw} units (${percentage}%)`;
                              },
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: {
                              color: "rgba(0, 0, 0, 0.05)",
                            },
                          },
                          y: {
                            grid: {
                              display: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Doughnut Chart - EPC Quality Distribution */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Gauge
                      className="text-moss-primary h-5 w-5"
                      weight="duotone"
                    />
                    EPC Kwaliteit Overzicht
                  </CardTitle>
                  <p className="text-muted-foreground text-sm">
                    Verdeling naar energieklasse
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="flex h-[280px] items-center justify-center">
                    <Doughnut
                      data={{
                        labels: [
                          "Goed (A-B)",
                          "Matig (C-D)",
                          "Slecht (E-G)",
                          "Onbekend/Ontbrekend",
                        ],
                        datasets: [
                          {
                            data: [
                              // Good: A++, A+, A, B
                              Object.entries(epcMetrics.epcDistribution || {})
                                .filter(([label]) =>
                                  ["A++", "A+", "A", "B"].includes(label),
                                )
                                .reduce(
                                  (sum, [, count]) => sum + (count as number),
                                  0,
                                ),
                              // Medium: C, D
                              Object.entries(epcMetrics.epcDistribution || {})
                                .filter(([label]) => ["C", "D"].includes(label))
                                .reduce(
                                  (sum, [, count]) => sum + (count as number),
                                  0,
                                ),
                              // Bad: E, F, G
                              Object.entries(epcMetrics.epcDistribution || {})
                                .filter(([label]) =>
                                  ["E", "F", "G"].includes(label),
                                )
                                .reduce(
                                  (sum, [, count]) => sum + (count as number),
                                  0,
                                ),
                              // Unknown/Missing
                              Object.entries(epcMetrics.epcDistribution || {})
                                .filter(([label]) =>
                                  [
                                    "Onbekend",
                                    "Niet van toepassing",
                                    "Ontbrekend",
                                  ].includes(label),
                                )
                                .reduce(
                                  (sum, [, count]) => sum + (count as number),
                                  0,
                                ),
                            ],
                            backgroundColor: [
                              COLORS.moss,
                              COLORS.yellow,
                              COLORS.brick,
                              "#D1D5DB",
                            ],
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "60%",
                        plugins: {
                          legend: {
                            position: "right" as const,
                            labels: {
                              usePointStyle: true,
                              padding: 15,
                              generateLabels: (chart: any) => {
                                const data = chart.data;
                                if (
                                  data.labels.length &&
                                  data.datasets.length
                                ) {
                                  const total = data.datasets[0].data.reduce(
                                    (a: number, b: number) => a + b,
                                    0,
                                  );
                                  return data.labels.map(
                                    (label: string, i: number) => {
                                      const value = data.datasets[0].data[i];
                                      const percentage =
                                        total > 0
                                          ? Math.round((value / total) * 100)
                                          : 0;
                                      return {
                                        text: `${label}: ${value} (${percentage}%)`,
                                        fillStyle:
                                          data.datasets[0].backgroundColor[i],
                                        hidden: false,
                                        index: i,
                                        pointStyle: "circle",
                                      };
                                    },
                                  );
                                }
                                return [];
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Costs Section Header */}
          <div className="mt-8 mb-6 flex items-center gap-3">
            <div className="bg-brick-primary/10 rounded-lg p-2">
              <CurrencyEur
                className="text-brick-primary h-6 w-6"
                weight="duotone"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">
                Kosten {new Date().getFullYear()}
              </h2>
              <p className="text-muted-foreground text-sm">
                OPEX, CAPEX en energiekosten dit jaar
              </p>
            </div>
          </div>

          {/* Costs KPI Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* OPEX */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  OPEX Kosten
                </CardTitle>
                <CurrencyEur
                  className="text-brick-primary h-4 w-4"
                  weight="duotone"
                />
              </CardHeader>
              <CardContent>
                <div className="text-brick-primary text-2xl font-bold">
                  {costs.opexCosts !== null ? (
                    applyMask(-costs.opexCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  {costs.opexPercentage !== null
                    ? `${costs.opexPercentage.toFixed(1)}% van huurinkomsten`
                    : "Niet-doorbelastbare kosten"}
                </p>
              </CardContent>
            </Card>

            {/* CAPEX */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  CAPEX Kosten
                </CardTitle>
                <Wrench
                  className="text-neutral-primary h-4 w-4"
                  weight="duotone"
                />
              </CardHeader>
              <CardContent>
                <div className="text-neutral-primary text-2xl font-bold">
                  {costs.capexCosts !== null ? (
                    applyMask(-costs.capexCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Investeringskosten
                </p>
              </CardContent>
            </Card>

            {/* Heating Costs */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  Verwarming
                </CardTitle>
                <Flame className="h-4 w-4 text-orange-500" weight="duotone" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {costs.heatingCosts !== null ? (
                    applyMask(-costs.heatingCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  612010 - Kosten verwarming
                </p>
              </CardContent>
            </Card>

            {/* Electricity Costs */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  Elektriciteit
                </CardTitle>
                <Lightning
                  className="h-4 w-4 text-yellow-500"
                  weight="duotone"
                />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {costs.electricityCosts !== null ? (
                    applyMask(-costs.electricityCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  612020 - Kosten elektriciteit
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Second row of cost cards */}
          <div className="mb-24 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Water Costs */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  Waterverbruik
                </CardTitle>
                <Drop className="h-4 w-4 text-blue-500" weight="duotone" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {costs.waterCosts !== null ? (
                    applyMask(-costs.waterCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  612030 - Kosten waterverbruik
                </p>
              </CardContent>
            </Card>

            {/* Total Energy Costs */}
            <Card
              className={
                costs.totalEnergyCosts !== null
                  ? "bg-moss-secondary-light"
                  : "bg-white"
              }
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  Totaal Energie
                </CardTitle>
                <Gauge className="text-moss-primary h-4 w-4" weight="duotone" />
              </CardHeader>
              <CardContent>
                <div className="text-moss-primary text-2xl font-bold">
                  {costs.totalEnergyCosts !== null ? (
                    applyMask(-costs.totalEnergyCosts, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Som van verwarming, elektriciteit en water
                </p>
              </CardContent>
            </Card>

            {/* Rent Income (for context) */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  Huurinkomsten
                </CardTitle>
                <TrendUp
                  className="text-moss-primary h-4 w-4"
                  weight="duotone"
                />
              </CardHeader>
              <CardContent>
                <div className="text-moss-primary text-2xl font-bold">
                  {costs.invoicedRentLines !== null ? (
                    applyMask(costs.invoicedRentLines, "valuta")
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Gefactureerde huurlijnen
                </p>
              </CardContent>
            </Card>

            {/* OPEX Percentage */}
            <Card
              className={
                costs.opexPercentage !== null
                  ? costs.opexPercentage <= 10
                    ? "bg-moss-secondary-light"
                    : costs.opexPercentage <= 15
                      ? "bg-yellow-50"
                      : "bg-brick-secondary-fine"
                  : "bg-white"
              }
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  OPEX Ratio
                </CardTitle>
                <ChartBar
                  className="text-neutral-primary h-4 w-4"
                  weight="duotone"
                />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    costs.opexPercentage !== null
                      ? costs.opexPercentage <= 10
                        ? "text-moss-primary"
                        : costs.opexPercentage <= 15
                          ? "text-yellow-600"
                          : "text-brick-primary"
                      : "text-neutral-primary"
                  }`}
                >
                  {costs.opexPercentage !== null ? (
                    `${costs.opexPercentage.toFixed(1)}%`
                  ) : costsLoading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    "—"
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  OPEX / Huurinkomsten
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenanceStatistieken;
